import React, { useEffect } from 'react';
import { Image } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';

import { DropDownWithDefaultSelect } from 'components/base/ActionsDropdown';
import CustomDateTimeSelect from 'components/base/CustomDateTimeSelect';
import {
  CustomErrorMessage,
  hasError,
  hasReactHookFormError,
} from 'components/base/ErrorFieldMessage';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';
import { PERIOD_TYPE_DISPLAY } from 'config/CustomEnums';

import BranchLine from 'assets/images/branchLinex3.png';

import './InputWithMutipleDropDown.scss';

const defaultRangeOptions = [
  PERIOD_TYPE_DISPLAY.ALL_THE_TIME,
  PERIOD_TYPE_DISPLAY.WITHIN,
  PERIOD_TYPE_DISPLAY.EQUAL_TO,
  PERIOD_TYPE_DISPLAY.BETWEEN,
  PERIOD_TYPE_DISPLAY.AFTER,
  PERIOD_TYPE_DISPLAY.BEFORE,
];

export default function InputWithMutipleDropDown({
  title,
  fieldBaseName,
  hardCodeZeroNumber,
  hasNumber,
  numberMin,
  rangeOptions,
  hasPeriodDropdown,
  timeSelectorDescription,
  requireFutureTime,
}) {
  const {
    setValue,
    setError,
    clearErrors,
    control,
    watch,
    register,
    formState: { errors },
  } = useFormContext();
  const fixedRangeOptions = rangeOptions || defaultRangeOptions;

  const numberFieldName = `${fieldBaseName}.number`;
  const periodRangeFieldName = `${fieldBaseName}.periodRange`;
  const periodMinDateFieldName = `${fieldBaseName}.minDate`;
  const periodMaxDateFieldName = `${fieldBaseName}.maxDate`;
  const periodNumberOfDaysFieldName = `${fieldBaseName}.numberOfDays`;

  const periodRange = watch(periodRangeFieldName) || 'Please select';
  const periodMinDate = watch(periodMinDateFieldName);
  const periodMaxDate = watch(periodMaxDateFieldName);

  const showPeriodMinDate = [
    PERIOD_TYPE_DISPLAY.EQUAL_TO,
    PERIOD_TYPE_DISPLAY.BETWEEN,
    PERIOD_TYPE_DISPLAY.GREATER_THAN,
    PERIOD_TYPE_DISPLAY.LESS_THAN,
    PERIOD_TYPE_DISPLAY.BEFORE,
    PERIOD_TYPE_DISPLAY.AFTER,
  ].includes(periodRange);
  const showPeriodMaxDate = periodRange === PERIOD_TYPE_DISPLAY.BETWEEN;

  const dropDownOptions = (values, fieldName) => {
    return values.map((value) => ({
      name: value,
      action: () => {
        setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
      },
    }));
  };

  const validateMinDate = () => {
    clearErrors(periodMinDateFieldName);
    const now = new Date()
    const minDateIsNotFutureTime = !periodMinDate || new Date(periodMinDate) < now
    if (requireFutureTime && showPeriodMinDate && minDateIsNotFutureTime) {
      setError(periodMinDateFieldName, {
        type: 'manual',
        message:
          'Please choose a future time.',
      });
      return false;
    }
    return true;
  }

  const checkValidDate = () => {
    clearErrors(periodMaxDateFieldName);
    if (periodRange === PERIOD_TYPE_DISPLAY.BETWEEN) {
      if (
        periodMinDate &&
        periodMaxDate &&
        new Date(periodMaxDate) <= new Date(periodMinDate)
      ) {
        setError(periodMaxDateFieldName, {
          type: 'manual',
          message:
            'The end date and time cannot be on or before the start date and time.',
        });
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    checkValidDate();
    validateMinDate();
  }, [periodMinDate, periodMaxDate]);

  useEffect(() => {
    if (hardCodeZeroNumber) {
      setValue(numberFieldName, 1, { shouldDirty: true, shouldValidate: true });
    }
  }, [hardCodeZeroNumber]);

  return (
    <div>
      {title ? <CustomTitleLabel title={title} /> : null}
      <div style={{ display: 'flex' }}>
        {hardCodeZeroNumber ? (
          <label className="radio-button-label zero-number-label">
            0 times
          </label>
        ) : null}
        {hasNumber ? (
          <input
            type={hardCodeZeroNumber ? 'hidden' : 'number'}
            min={numberMin}
            className={`custom-number-input-long ${hasReactHookFormError(errors, numberFieldName)
                ? 'error-field'
                : ''
              }`}
            {...register(numberFieldName, { required: hasNumber })}
          />
        ) : null}

        <Controller
          control={control}
          rules={{ required: fixedRangeOptions.length !== 1 }}
          name={periodRangeFieldName}
          render={({ field }) => (
            <DropDownWithDefaultSelect
              actions={dropDownOptions(fixedRangeOptions, periodRangeFieldName)}
              defaultValue={periodRange}
              className={
                hasReactHookFormError(errors, field.name) ? 'error-field' : ''
              }
            />
          )}
        />
      </div>
      {periodRange === PERIOD_TYPE_DISPLAY.WITHIN && hasPeriodDropdown ? (
        <div className="d-flex">
          <Image src={BranchLine} className="content-campaign-branchLine" />
          <div className="period-date-with-line-margin">
            <div className="d-flex">
              <input
                type="number"
                min="0"
                className={`custom-number-input-long ${hasReactHookFormError(errors, periodNumberOfDaysFieldName)
                  ? 'error-field'
                  : ''
                  }`}
                {...register(periodNumberOfDaysFieldName, {
                  required: 'This field is required.',
                })}
              />
              <label className="content-campaign-related-to margin-right-10-percent">
                number of days
              </label>
            </div>
            <CustomErrorMessage name={periodNumberOfDaysFieldName} />
          </div>
        </div>
      ) : null}
      {showPeriodMinDate && (
        <div className="d-flex">
          <Image src={BranchLine} className="content-campaign-branchLine" />
          <div className="period-date-with-line-margin">
            <Controller
              control={control}
              name={periodMinDateFieldName}
              rules={{ 
                required: true,
                validate: (value) => {
                  return validateMinDate()
                }
              }}
              render={({ field }) => (
                <>
                  <CustomDateTimeSelect
                    defaultTime={periodMinDate ? new Date(periodMinDate) : null}
                    error={hasError(errors, periodMinDateFieldName)}
                    onTimeChange={(date) => {
                      setValue(periodMinDateFieldName, date, {
                        shouldDirty: true,
                      });
                    }}
                  />
                  <CustomErrorMessage name={periodMinDateFieldName} />
                </>
              )}
            />
            {showPeriodMaxDate && (
              <>
                <label className="content-campaign-related-to period-date-margin">
                  to
                </label>
                <div className="period-date-margin">
                  <Controller
                    control={control}
                    name={periodMaxDateFieldName}
                    rules={{
                      required: true,
                      validate: (value) => {
                        return checkValidDate();
                      },
                    }}
                    render={({ field }) => (
                      <>
                        <CustomDateTimeSelect
                          defaultTime={
                            periodMaxDate ? new Date(periodMaxDate) : null
                          }
                          error={hasError(errors, periodMaxDateFieldName)}
                          onTimeChange={(date) => {
                            setValue(periodMaxDateFieldName, date, {
                              shouldDirty: true,
                            });
                          }}
                        />
                        <CustomErrorMessage name={periodMaxDateFieldName} />
                      </>
                    )}
                  />
                </div>
              </>
            )}
            {timeSelectorDescription && <label className="tips-message">{timeSelectorDescription}</label>}
          </div>
        </div>
      )}
    </div>
  );
}
